import { SKY_CONDITION_OPTIONS } from './skyConditionMapping';

// Sky condition normalization helper
// Converts API label format to dropdown value format
export const normalizeSkyCondition = (skyCondition: string): string => {
  if (!skyCondition) return '';

  const skyOption = SKY_CONDITION_OPTIONS.find(
    opt => opt.label === skyCondition || opt.value === skyCondition
  );

  return skyOption?.value || skyCondition;
};

// Temperature conversion helpers
export const convertCelsiusToFahrenheit = (celsius: number): number => {
  return (celsius * 9) / 5 + 32;
};

export const convertFahrenheitToCelsius = (fahrenheit: number): number => {
  return ((fahrenheit - 32) * 5) / 9;
};

export const convertTemperature = (
  value: number,
  fromUnit: string,
  toUnit: string
): number => {
  if (fromUnit === toUnit) return value;

  if (fromUnit === '°C' && toUnit === '°F') {
    return convertCelsiusToFahrenheit(value);
  }
  if (fromUnit === '°F' && toUnit === '°C') {
    return convertFahrenheitToCelsius(value);
  }

  return value;
};

// Helper function to normalize temperature unit values
export const normalizeTemperatureUnit = (unit: string): string => {
  if (!unit) return '°F'; // Default to Fahrenheit

  const normalizedUnit = unit.toLowerCase().trim();

  // Map various possible API values to our standard format
  if (
    normalizedUnit === 'c' ||
    normalizedUnit === '°c' ||
    normalizedUnit === 'celsius'
  ) {
    return '°C';
  }
  if (
    normalizedUnit === 'f' ||
    normalizedUnit === '°f' ||
    normalizedUnit === 'fahrenheit'
  ) {
    return '°F';
  }

  // If it's already in our format, return as-is
  if (unit === '°C' || unit === '°F') {
    return unit;
  }

  // Default fallback
  return '°F';
};

// Helper function to normalize percentage unit values
export const normalizePercentageUnit = (unit: string): string => {
  if (!unit) return '%'; // Default to percentage symbol

  const normalizedUnit = unit.toLowerCase().trim();

  // Map various possible API values to our standard format
  if (
    normalizedUnit === 'pct' ||
    normalizedUnit === 'percent' ||
    normalizedUnit === 'percentage'
  ) {
    return '%';
  }

  // If it's already in our format, return as-is
  if (unit === '%') {
    return unit;
  }

  // Default fallback
  return '%';
};

// Wind speed conversion helpers
export const convertMpsToMph = (mps: number): number => {
  return mps * 2.23694;
};

export const convertMphToMps = (mph: number): number => {
  return mph / 2.23694;
};

export const convertMpsToKmh = (mps: number): number => {
  return mps * 3.6;
};

export const convertKmhToMps = (kmh: number): number => {
  return kmh / 3.6;
};

export const convertMphToKmh = (mph: number): number => {
  return mph * 1.609344;
};

export const convertKmhToMph = (kmh: number): number => {
  return kmh / 1.609344;
};

export const convertWindSpeed = (
  value: number,
  fromUnit: string,
  toUnit: string
): number => {
  // Return original value if either unit is missing

  if (fromUnit === toUnit) return value;

  // Normalize common unit variations (handles both uppercase and lowercase)
  const normalizeUnit = (unit: string): string => {
    if (!unit) return unit;
    const upperUnit = unit.toUpperCase();
    if (upperUnit === 'M/S' || upperUnit === 'MPS') return 'MPS';
    if (upperUnit === 'MPH') return 'MPH';
    if (upperUnit === 'KMH' || upperUnit === 'KM/H') return 'KMH';
    return unit;
  };

  const fromNormalized = normalizeUnit(fromUnit);
  const toNormalized = normalizeUnit(toUnit);

  if (fromNormalized === toNormalized) return value;

  // Convert from source unit to m/s first, then to target unit
  let valueInMps = value;

  // Convert to MPS (m/s)
  if (fromNormalized === 'MPH') {
    valueInMps = convertMphToMps(value);
  } else if (fromNormalized === 'KMH') {
    valueInMps = convertKmhToMps(value);
  }

  // Convert from MPS to target unit
  if (toNormalized === 'MPH') {
    return convertMpsToMph(valueInMps);
  }
  if (toNormalized === 'KMH') {
    return convertMpsToKmh(valueInMps);
  }

  return valueInMps;
};

// Temperature unit options
export const TEMPERATURE_UNITS = [
  { label: 'Celsius', value: '°C' },
  { label: 'Fahrenheit', value: '°F' }
];

// Helper function to normalize wind speed unit values
export const normalizeWindSpeedUnit = (unit: string): string => {
  if (!unit) return 'MPH'; // Default to MPH

  const normalizedUnit = unit.toLowerCase().trim();

  // Map various possible API values to our standard uppercase format
  if (
    normalizedUnit === 'm/s' ||
    normalizedUnit === 'mps' ||
    normalizedUnit === 'meters per second'
  ) {
    return 'MPS';
  }
  if (normalizedUnit === 'mph' || normalizedUnit === 'miles per hour') {
    return 'MPH';
  }
  if (
    normalizedUnit === 'km/h' ||
    normalizedUnit === 'kmh' ||
    normalizedUnit === 'kilometers per hour'
  ) {
    return 'KMH';
  }

  // If it's already in uppercase format, return as-is
  if (unit === 'MPS' || unit === 'MPH' || unit === 'KMH') {
    return unit;
  }

  // Default fallback
  return 'MPH';
};

// Wind speed unit options
export const WIND_SPEED_UNITS = [
  { label: 'MPS', value: 'MPS' },
  { label: 'MPH', value: 'MPH' },
  { label: 'KMH', value: 'KMH' }
];

export {
  WIND_DIRECTION_OPTIONS,
  getWindDirectionLabel,
  getWindDirectionShortLabel
} from './windDirectionMapping';

export {
  SOIL_MOISTURE_OPTIONS,
  getsoilMoistureCategoryFromPercentage,
  getSoilMoistureLabel,
  getSoilMoistureShortLabel,
  getSoilMoisturePercentageRange
} from './soilMoistureMapping';
